import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { createUserWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { auth } from './firebase';
import { createUserDocument, getUserData } from './utils/userService';
import CheckoutSignUp from './CheckoutSignUp';
import CheckoutBillingInfo from './CheckoutBillingInfo';
import LoginPage from './LoginPage';
import './CheckoutPage.css'; // Stil dosyanızı dahil edin

const CheckoutPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeStep, setActiveStep] = useState(0); // 0: Fatura, 1: Ödeme
  const [loading, setLoading] = useState(false);
  const [checkoutFormContent, setCheckoutFormContent] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [isUserAuthenticated, setIsUserAuthenticated] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponError, setCouponError] = useState('');
  
  // Step 0 - Üyelik Bilgileri için state'ler
  const [userFormData, setUserFormData] = useState({
    firstName: '',
    email: '',
    password: '',
    confirmPassword: '',
    phoneNumber: ''
  });
  const [userFormErrors, setUserFormErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [userData, setUserData] = useState(null);
  
  // Step 1 - Fatura Bilgileri için state'ler
  const [billingData, setBillingData] = useState({
    name: '',
    surname: '',
    identityNumber: '',
    address: '',
    city: '',
    country: 'Türkiye'
  });
  const [billingErrors, setBillingErrors] = useState({});

  // Paket bilgileri (Statik olarak tanımlandı, genellikle API'den gelir)
  const packageInfo = {
    name: 'Yaşasın Hamileyim Eğitimi',
    originalPrice: 6000,
    discountedPrice: 3900,
    features: [
      '6-40. Hafta Gebelik Kılavuzu',
      'Doğuma Hazırlık Eğitimi',
      'Bebek Sağlığı ve Bakımı',
      'Emzirme Eğitimi',
      'Hamilelikte Beslenme',
      'Hamileliğe Psikolojik Yaklaşım',
      'Hamile Pilatesi',
      'HamilelikteNefes',
      'Doğumda Anestezi',
      'Yurtdışında Doğum',
      'Oda Süsleme ve Doğum Fotoğrafçılığı Bilgilendirme'
    ]
  };

  // Kupon uygulandığında ekranda gösterilecek aylık ödeme (%50 indirim)
  const discountPercentage = 50;
  const monthlyPayment = appliedCoupon ? 194 : 388; // Kupon ile aylık 194 TL (%50 indirim)

  // 1. İyzico Callback Fonksiyonu
  const handlePaymentCallback = useCallback(async (result) => {
    // Bu kısım iyzico formu tarafından tetiklenir, tarayıcıda çalışır.
    
    // Ödeme formu başarılı şekilde tamamlandıysa, backend'e doğrulama gönder.
    if (!result || !result.token) {
        console.error("Callback Hatası: Token bulunamadı.");
        navigate('/payment-failed', { state: { error: 'Ödeme verisi eksik.' } });
        return;
    }
    
    setLoading(true);

    try {
      // Backend'deki paymentCallback fonksiyonunu çağır (Ödeme doğrulaması için)
      const response = await fetch('https://europe-west1-yasasinhamileyim.cloudfunctions.net/paymentCallback', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token: result.token,
          // ConversationId genelde callback body'sinde gelmez, 
          // ancak iyzico'dan dönüyorsa göndermek daha iyidir.
          conversationId: result.conversationId 
        })
      });

      // Callback fonksiyonunda genelde backend'den redirect beklenir, 
      // ama biz Cloud Function'ı json döndürmeye göre tasarladık (Örnek kod).
      if (!response.ok) {
        throw new Error('Backend doğrulama hatası');
      }

      const data = await response.json();

      if (data.status === 'success') {
        // ✅ Ödeme başarılı ve backend onayladı
        // Kupon kodunu temizle (tek kullanımlık)
        localStorage.removeItem('referral_coupon_code');
        console.log('✅ Ödeme başarılı - Kupon kodu temizlendi');
        navigate('/payment-success', { state: { paymentId: data.paymentId } });
      } else {
        // ❌ Ödeme başarısız (iyzico'dan dönen hata)
        navigate('/payment-failed', { state: { error: data.errorMessage || 'Ödeme doğrulanamadı.' } });
      }

    } catch (error) {
      console.error('Doğrulama Callback Hatası:', error);
      navigate('/payment-failed', { state: { error: 'Sunucuya bağlanılamadı. Lütfen destek ile iletişime geçin.' } });
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  // 2. Checkout Formu Yükleme Fonksiyonu
  const loadCheckoutForm = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    setCheckoutFormContent(null);
    
    try {
      // Kullanıcı bilgilerini al (auth'dan)
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('Kullanıcı giriş yapmamış');
      }

      const response = await fetch('https://europe-west1-yasasinhamileyim.cloudfunctions.net/createPayment', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          // Fiyat bilgileri backend'de Firestore'dan alınıyor, güvenlik için buradan göndermiyoruz
          billingData: billingData,
          userEmail: currentUser.email,
          userId: currentUser.uid,
          couponCode: appliedCoupon || null
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.errorMessage || `Backend hatası: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.checkoutFormContent) {
        setCheckoutFormContent(data.checkoutFormContent);
      } else {
        throw new Error('İyzico form içeriği alınamadı.');
      }
      
    } catch (error) {
      console.error('Checkout form yükleme hatası:', error);
      setLoadError(`Ödeme formu yüklenemedi: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }, [packageInfo.discountedPrice, billingData]);

  // 2.4. Referral kupon kodunu localStorage'dan oku ve otomatik uygula
  useEffect(() => {
    const savedCouponCode = localStorage.getItem('referral_coupon_code');
    if (savedCouponCode) {
      const normalizedCode = savedCouponCode.trim().toLowerCase();
      if (normalizedCode === 'yasasin50') {
        setCouponCode(savedCouponCode);
        setAppliedCoupon(savedCouponCode);
        setCouponError('');
        console.log('✅ Referral kupon kodu otomatik uygulandı:', savedCouponCode);
      }
    }
  }, []); // Sadece component mount olduğunda bir kez çalış

  // 2.5. URL parametrelerinden error ve step'i oku (Ödeme başarısız olduğunda backend'den gelen)
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const error = searchParams.get('error');
    const errorCode = searchParams.get('errorCode');
    const stepParam = searchParams.get('step');
    const couponParam = searchParams.get('code'); // URL'den direkt kupon kodu gelirse
    
    // URL'den kupon kodu gelirse (yeni kod veya güncelleme)
    if (couponParam) {
      const normalizedCode = couponParam.trim().toLowerCase();
      if (normalizedCode === 'yasasin50') {
        localStorage.setItem('referral_coupon_code', normalizedCode);
        setCouponCode(normalizedCode);
        setAppliedCoupon(normalizedCode);
        setCouponError('');
        console.log('✅ URL\'den kupon kodu uygulandı:', normalizedCode);
        
        // URL'den code parametresini temizle
        const newSearchParams = new URLSearchParams(location.search);
        newSearchParams.delete('code');
        const newUrl = newSearchParams.toString() 
          ? `${location.pathname}?${newSearchParams.toString()}` 
          : location.pathname;
        navigate(newUrl, { replace: true });
      }
    }
    
    if (error) {
      // Error mesajını göster
      setLoadError(decodeURIComponent(error));
      console.error('Ödeme hatası:', decodeURIComponent(error), errorCode);
      
      // Step parametresi varsa activeStep'i ayarla
      if (stepParam) {
        const step = parseInt(stepParam, 10);
        if (!isNaN(step) && step >= 0 && step <= 2) {
          setActiveStep(step);
        }
      }
      
      // State'leri localStorage'dan geri yükle (eğer varsa)
      try {
        const savedBillingData = localStorage.getItem('checkout_billingData');
        if (savedBillingData) {
          setBillingData(JSON.parse(savedBillingData));
        }
        
        const savedUserFormData = localStorage.getItem('checkout_userFormData');
        if (savedUserFormData) {
          setUserFormData(JSON.parse(savedUserFormData));
        }
      } catch (e) {
        console.error('LocalStorage\'dan state geri yüklenirken hata:', e);
      }
      
      // URL'den error parametresini temizle (tekrar gösterilmemesi için)
      const newSearchParams = new URLSearchParams(location.search);
      newSearchParams.delete('error');
      newSearchParams.delete('errorCode');
      newSearchParams.delete('step');
      navigate(`${location.pathname}${newSearchParams.toString() ? '?' + newSearchParams.toString() : ''}`, { replace: true });
    }
  }, [location.search, location.pathname, navigate]);

  // 2.6. State'leri localStorage'a kaydet (billingData ve userFormData değiştiğinde)
  useEffect(() => {
    if (billingData && Object.keys(billingData).length > 0) {
      localStorage.setItem('checkout_billingData', JSON.stringify(billingData));
    }
  }, [billingData]);

  useEffect(() => {
    if (userFormData && Object.keys(userFormData).length > 0) {
      localStorage.setItem('checkout_userFormData', JSON.stringify(userFormData));
    }
  }, [userFormData]);

  // 3. Firebase Auth Kontrolü - Kullanıcı giriş yapmış mı? Premium mu?
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setIsCheckingAuth(false);
      if (user) {
        // Kullanıcı giriş yapmış - UserData'yı bir kez al ve state'te tut
        try {
          const data = await getUserData(user.uid);
          setUserData(data);
          
          // Email'i billingData'ya ekle
          setBillingData(prev => ({
            ...prev,
            email: user.email || ''
          }));
          
          if (data && data.isPremium) {
            // Premium kullanıcı, direkt lecture sayfasına yönlendir
            //console.log('✅ Premium kullanıcı, /lectures sayfasına yönlendiriliyor');
            navigate('/lectures');
            return;
          }
        } catch (error) {
          console.error('Kullanıcı verisi alınırken hata:', error);
        }
        
        // Premium değil, Step 0'da kal (Fatura Bilgileri)
        setIsUserAuthenticated(true);
        // URL'den step parametresi yoksa Step 0'da kal
        const searchParams = new URLSearchParams(location.search);
        const stepParam = searchParams.get('step');
        if (!stepParam) {
          setActiveStep(0);
        }
        //console.log('📝 Premium değil, Step 0\'da kalınıyor (Fatura Bilgileri)');
      } else {
        // Kullanıcı giriş yapmamış
        setIsUserAuthenticated(false);
        setUserData(null);
        // URL'den step parametresi yoksa Step 0'da kal
        const searchParams = new URLSearchParams(location.search);
        const stepParam = searchParams.get('step');
        if (!stepParam) {
          setActiveStep(0); // Step 0'da kal (Fatura Bilgileri)
        }
        //console.log('📝 Step 0\'da kalınıyor (Fatura Bilgileri)');
      }
    });

    return () => unsubscribe();
  }, [navigate, location.search]);

  // 4. Step 0 - Üyelik Formu Validasyonu
  const validateUserForm = () => {
    const errors = {};
    
    if (!userFormData.firstName.trim()) {
      errors.firstName = 'Ad Soyad gereklidir.';
    }
    
    if (!userFormData.email.trim()) {
      errors.email = 'E-posta adresi gereklidir.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userFormData.email)) {
      errors.email = 'Geçerli bir e-posta adresi giriniz.';
    }
    
    if (!userFormData.password) {
      errors.password = 'Şifre gereklidir.';
    } else if (userFormData.password.length < 6) {
      errors.password = 'Şifre en az 6 karakter olmalıdır.';
    }
    
    if (userFormData.password !== userFormData.confirmPassword) {
      errors.confirmPassword = 'Şifreler eşleşmiyor.';
    }
    
    if (userFormData.phoneNumber && !/^[0-9+\-\s()]+$/.test(userFormData.phoneNumber)) {
      errors.phoneNumber = 'Geçerli bir telefon numarası giriniz.';
    }
    
    setUserFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // 5. Step 0 - Üyelik Formu Submit
  const handleUserFormSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateUserForm()) {
      return;
    }

    setLoading(true);
    setUserFormErrors({});

    try {
      // Firebase'de kullanıcı oluştur
      const credential = await createUserWithEmailAndPassword(
        auth,
        userFormData.email,
        userFormData.password
      );

      // Display name güncelle
      if (userFormData.firstName.trim()) {
        await updateProfile(credential.user, {
          displayName: userFormData.firstName.trim()
        });
      }

      // Firestore'da user document oluştur
      await createUserDocument(
        credential.user.uid,
        userFormData.email,
        userFormData.firstName.trim(),
        userFormData.phoneNumber.trim() || ''
      );

      // Başarılı, Step 1'e geç
      setIsUserAuthenticated(true);
      setActiveStep(1);
      
      // Email'i billingData'ya ekle (kullanıcıdan alınmış)
      setBillingData(prev => ({
        ...prev,
        email: userFormData.email
      }));
    } catch (error) {
      const errorMessages = {
        'auth/email-already-in-use': 'Bu e-posta adresiyle kayıtlı bir hesap zaten var.',
        'auth/weak-password': 'Şifre en az 6 karakter olmalıdır.',
        'auth/invalid-email': 'Geçerli bir e-posta adresi giriniz.'
      };
      setUserFormErrors({
        general: errorMessages[error.code] || 'Kayıt olurken bir hata oluştu. Lütfen tekrar deneyiniz.'
      });
    } finally {
      setLoading(false);
    }
  };

  // 6. Step 1 - Fatura Bilgileri Validasyonu
  const validateBillingForm = () => {
    const errors = {};
    
    if (!billingData.name.trim()) {
      errors.name = 'Ad gereklidir.';
    }
    
    if (!billingData.surname.trim()) {
      errors.surname = 'Soyad gereklidir.';
    }
    
    if (!billingData.identityNumber.trim()) {
      errors.identityNumber = 'TC Kimlik No gereklidir.';
    } else if (billingData.identityNumber.length !== 11) {
      errors.identityNumber = 'TC Kimlik No 11 haneli olmalıdır.';
    }
    
    if (!billingData.address.trim()) {
      errors.address = 'Adres gereklidir.';
    }
    
    if (!billingData.city.trim()) {
      errors.city = 'Şehir gereklidir.';
    }
    
    if (!billingData.country.trim()) {
      errors.country = 'Ülke gereklidir.';
    }
    
    setBillingErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // 7. Step 1 - Fatura Bilgileri Submit
  const handleBillingFormSubmit = (e) => {
    e.preventDefault();
    
    // Login kontrolü
    if (!isUserAuthenticated) {
      setBillingErrors({
        general: 'Ödeme işlemine devam etmek için lütfen giriş yapın.'
      });
      setShowLoginModal(true); // Login modal'ını aç
      return;
    }
    
    if (!validateBillingForm()) {
      return;
    }


    // Başarılı, Step 1'e geç (Ödeme)
    setActiveStep(1);
  };

  // 8. Ödeme formunu sadece Step 1'de yükle
  useEffect(() => {
    document.title = 'Ödeme - Yaşasın Hamileyim';
    if (activeStep === 1 && isUserAuthenticated) {
      loadCheckoutForm();
    }
  }, [activeStep, isUserAuthenticated, loadCheckoutForm]);


  const steps = [
    { label: 'Fatura Bilgileri', step: 0 },
    { label: 'Ödeme', step: 1 }
  ];

  return (
    <div className="checkout-page">
      {/* Stepper - En Üstte, Tam Genişlik */}
      <div style={{ 
        width: '100%', 
        backgroundColor: '#f8f9fa', 
        padding: '30px 0',
        marginBottom: '40px'
      }}>
        <div className="container">
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            position: 'relative',
            maxWidth: '800px',
            margin: '0 auto'
          }}>
            {/* Çizgiler */}
            <div style={{
              position: 'absolute',
              top: '20px',
              left: '10%',
              right: '10%',
              height: '2px',
              backgroundColor: '#e0e0e0',
              zIndex: 0
            }}>
              {/* Aktif çizgi */}
              <div style={{
                height: '100%',
                backgroundColor: activeStep > 0 ? '#4caf50' : '#9e9e9e',
                width: `${(activeStep / (steps.length - 1)) * 100}%`,
                transition: 'width 0.3s ease'
              }} />
            </div>

            {/* Adımlar */}
            {steps.map((step, index) => {
              const isActive = activeStep === step.step;
              const isCompleted = activeStep > step.step;
              
              // Renk belirleme: Tamamlanan = yeşil, Aktif = gri, Pasif = gri
              const circleColor = isCompleted ? '#4caf50' : '#9e9e9e';
              const labelColor = isCompleted ? '#4caf50' : '#9e9e9e';
              
              return (
                <div key={step.step} style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  flex: 1,
                  position: 'relative',
                  zIndex: 1
                }}>
                  {/* Daire */}
                  <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    backgroundColor: circleColor,
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold',
                    fontSize: '16px',
                    marginBottom: '10px',
                    transition: 'all 0.3s ease',
                    border: isActive ? '2px solid #757575' : 'none'
                  }}>
                    {isCompleted ? '✓' : step.step + 1}
                  </div>
                  
                  {/* Label */}
                  <span style={{
                    fontSize: '14px',
                    color: labelColor,
                    fontWeight: isActive ? 600 : 400,
                    textAlign: 'center',
                    transition: 'all 0.3s ease'
                  }}>
                    {step.label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Error Banner - Ödeme hatası varsa göster */}
      {loadError && (
        <div className="container" style={{ marginBottom: '20px' }}>
          <div style={{
            padding: '20px',
            backgroundColor: '#ffebee',
            border: '2px solid #d32f2f',
            borderRadius: '8px',
            color: '#c62828',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '15px'
          }}>
            <div style={{ flex: 1 }}>
              <strong style={{ fontSize: '16px', display: 'block', marginBottom: '5px' }}>
                ⚠️ Ödeme Hatası
              </strong>
              <p style={{ margin: 0, fontSize: '14px' }}>{loadError}</p>
            </div>
            <button
              type="button"
              onClick={() => {
                setLoadError(null);
                if (activeStep === 1) {
                  loadCheckoutForm();
                }
              }}
              style={{
                padding: '10px 20px',
                backgroundColor: '#d32f2f',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 'bold',
                fontSize: '14px'
              }}
              disabled={loading}
            >
              {loading ? 'Yükleniyor...' : 'Tekrar Dene'}
            </button>
          </div>
        </div>
      )}

      <div className="container">
        <div className="row" style={{ display: 'flex', flexWrap: 'wrap' }}>
          {/* Sağ Taraf - Sipariş Özeti (Mobilde üstte) - Sadece Step 0'da göster */}
          {activeStep === 0 && (
            <div className="col-12 col-md-5" style={{ order: 1 }}>
              <div className="checkout-summary">
              <div className="package-card">
                {/* Motivasyon Başlığı */}
                <div style={{
                  padding: '20px 15px',
                  background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                  borderRadius: '12px',
                  marginBottom: '20px',
                  borderLeft: '4px solid #667eea',
                  borderRight: '4px solid #667eea'
                }}>
                  <div style={{ 
                    fontSize: '1.15rem', 
                    color: '#333',
                    fontWeight: '700',
                    lineHeight: '1.6',
                    textAlign: 'center'
                  }}>
                    Hamileliğinizi korkuyla değil, güvenle yaşayın
                  </div>
                </div>

                {/* Özel Avantajlar */}
                <div className="pricing-benefits" style={{
                  //marginBottom: '25px',
                  padding: '15px',
                  //background: '#f7fafc',
                  borderRadius: '8px'
                }}>
                  <ul style={{ 
                    listStyle: 'none', 
                    padding: 0, 
                    margin: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '10px'
                  }}>
                    <li style={{ 
                      fontSize: '1rem', 
                      color: '#666',
                      display: 'flex',
                      alignItems: 'center',
                      fontWeight: '600',
                      gap: '8px'
                    }}>
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" style={{ flexShrink: 0 }}>
                        <circle cx="10" cy="10" r="10" fill="#22c55e"/>
                        <path d="M6 10L9 13L14 7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <span>1 hafta içinde iade hakkı</span>
                    </li>
                    <li style={{ 
                      fontSize: '1rem', 
                      color: '#666',
                      display: 'flex',
                      alignItems: 'center',
                      fontWeight: '600',
                      gap: '8px'
                    }}>
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" style={{ flexShrink: 0 }}>
                        <circle cx="10" cy="10" r="10" fill="#22c55e"/>
                        <path d="M6 10L9 13L14 7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <span>Sınırsız izleme hakkı</span>
                    </li>
                  </ul>
                    </div>
                
                {/* İndirim Kodu Alanı - COMMENTED OUT */}
                {/* <div style={{ marginTop: '10px', marginBottom: '10px' }}>
                  <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
                    <input
                      type="text"
                      value={couponCode}
                      onChange={(e) => {
                        setCouponCode(e.target.value);
                        setCouponError('');
                      }}
                      placeholder="İndirim kodu"
                      style={{ flex: 1, padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        // Kupon kodu kontrolü: trim + lowercase ile karşılaştır
                        const normalizedCode = couponCode.trim().toLowerCase();
                        if (normalizedCode === 'yasasin50') {
                          setAppliedCoupon(couponCode.trim());
                          setCouponError('');
                        } else {
                          setAppliedCoupon(null);
                          setCouponError('Geçersiz kod');
                        }
                      }}
                      style={{ padding: '8px 12px', borderRadius: '4px', border: 'none', background: '#667eea', color: '#fff', fontWeight: '600', cursor: 'pointer' }}
                    >
                      Uygula
                    </button>
                  </div>
                  {couponError && (
                    <div style={{ color: '#dc2626', fontSize: '0.8rem', marginTop: '4px' }}>
                      {couponError}
                    </div>
                  )}
                  {appliedCoupon && !couponError && (
                    <div style={{ color: '#16a34a', fontSize: '0.8rem', marginTop: '4px', fontWeight: '600' }}>
                      ✓ Kod uygulandı: {appliedCoupon} (%{discountPercentage} indirim)
                    </div>
                  )}
                </div> */}

                {/* Fiyat Gösterimi - Taksit Seçenekleri */}
                <div className="total-section" style={{ 
                  borderTop: '1px solid #eee', 
                  marginTop: '20px', 
                  paddingTop: '20px',
                  textAlign: 'center'
                }}>
                  {/* Taksit Seçenekleri Tablosu */}
                  <div style={{ 
                    marginTop: '0',
                    marginBottom: '20px',
                    background: '#ffffff',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    border: '1px solid #e5e7eb'
                  }}>
                    <table style={{ 
                      width: '100%', 
                      borderCollapse: 'collapse',
                      fontSize: '0.95rem'
                    }}>
                      <thead>
                        <tr style={{ 
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          color: 'white'
                        }}>
                          <th style={{ 
                            padding: '15px', 
                            textAlign: 'left',
                            fontWeight: '600',
                            fontSize: '1rem'
                          }}>Taksit Seçeneği</th>
                          <th style={{ 
                            padding: '15px', 
                            textAlign: 'right',
                            fontWeight: '600',
                            fontSize: '1rem'
                          }}>Toplam Tutar</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr style={{ 
                          background: 'white',
                          borderBottom: '1px solid #e5e7eb'
                        }}>
                          <td style={{ padding: '12px', color: '#374151', fontWeight: '600' }}>
                            Tek Çekim
                          </td>
                          <td style={{ 
                            padding: '12px',
                            textAlign: 'right',
                            fontWeight: '600',
                            color: '#374151'
                          }}>₺2.900,00</td>
                        </tr>
                        <tr style={{ 
                          background: '#f9fafb',
                          borderBottom: '1px solid #e5e7eb'
                        }}>
                          <td style={{ padding: '12px', color: '#374151' }}>
                            2 x ₺1.495,47
                          </td>
                          <td style={{ 
                            padding: '12px',
                            textAlign: 'right',
                            fontWeight: '600',
                            color: '#374151'
                          }}>₺2.990,94</td>
                        </tr>
                        <tr style={{ 
                          background: 'white',
                          borderBottom: '1px solid #e5e7eb'
                        }}>
                          <td style={{ padding: '12px', color: '#374151' }}>
                            3 x ₺1.016,70
                          </td>
                          <td style={{ 
                            padding: '12px',
                            textAlign: 'right',
                            fontWeight: '600',
                            color: '#374151'
                          }}>₺3.050,10</td>
                        </tr>
                        <tr style={{ 
                          background: '#f9fafb',
                          borderBottom: '1px solid #e5e7eb'
                        }}>
                          <td style={{ padding: '12px', color: '#374151' }}>
                            6 x ₺542,32
                          </td>
                          <td style={{ 
                            padding: '12px',
                            textAlign: 'right',
                            fontWeight: '600',
                            color: '#374151'
                          }}>₺3.253,92</td>
                        </tr>
                        <tr style={{ 
                          background: 'white'
                        }}>
                          <td style={{ padding: '12px', color: '#374151' }}>
                            9 x ₺387,44
                          </td>
                          <td style={{ 
                            padding: '12px',
                            textAlign: 'right',
                            fontWeight: '600',
                            color: '#374151'
                          }}>₺3.486,92</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  {/* Bilgi Notu */}
                  <div style={{ 
                    marginTop: '15px',
                    padding: '12px',
                    background: '#fff3cd',
                    borderRadius: '8px',
                    fontSize: '0.9rem',
                    color: '#856404',
                    lineHeight: '1.5',
                    border: '1px solid #ffeaa7'
                  }}>
                    <strong>💳 Taksit seçimi</strong> ödeme sayfasında kart bilgilerinizi girdikten sonra yapılacaktır
                  </div>
                </div>
              </div>
            </div>
          </div>
              )}

          {/* Sol Taraf - Form İçeriği (Auth durumuna göre değişir) */}
          <div className="col-12 col-md-7" style={{ order: 2 }}>
            <div className="checkout-form-wrapper">
              {/* Step 0: Fatura Bilgileri */}
              {activeStep === 0 && (
                <>
                  {/* Giriş Yap / Kayıt Ol Butonu - Sadece login değilse göster */}
                  {!isUserAuthenticated && (
                    <div style={{ 
                      marginBottom: '20px', 
                      padding: '15px', 
                      backgroundColor: '#ffffff', 
                      borderRadius: '8px',
                      textAlign: 'center'
                    }}>
                      <p style={{ 
                        marginBottom: '12px', 
                        fontSize: '0.95rem', 
                        color: '#666',
                        lineHeight: '1.5'
                      }}>
                        Üyeliğiniz için bir hesap gerekli. 1 dakika içinde hesap oluşturabilirsiniz.
                      </p>
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => setShowLoginModal(true)}
                        style={{ 
                          padding: '12px 24px', 
                          fontSize: '1rem',
                          fontWeight: '600',
                          borderRadius: '8px',
                          border: 'none',
                          cursor: 'pointer'
                        }}
                      >
                        Giriş Yap / Kayıt Ol
                      </button>
                    </div>
                  )}
                  
                    <CheckoutBillingInfo
                      billingData={billingData}
                      setBillingData={setBillingData}
                      billingErrors={billingErrors}
                      onSubmit={handleBillingFormSubmit}
                      loading={loading}
                    />
                </>
                  )}

              {/* Step 1: Ödeme */}
              {activeStep === 1 && (
                    <div className="checkout-form">
                      <h2 className="checkout-title">Ödeme Bilgileri</h2>
                      
                      {/* Taksit Seçenekleri Bilgisi */}
                      <div style={{ 
                        marginBottom: '20px', 
                        padding: '15px', 
                        backgroundColor: '#f8f9fa', 
                        borderRadius: '8px',
                        fontSize: '0.95rem',
                        color: '#555',
                        lineHeight: '1.5'
                      }}>
                        <strong style={{ color: '#333' }}>💳 Taksit seçenekleri</strong> kart numaranızı girince otomatik olarak gözükecektir.
                      </div>
                      
                      <div className="form-section">
                        <div id="iyzipay-checkout-form" className="iyzipay-form">
                    
                    {loading && (
                      <div style={{ padding: '40px', textAlign: 'center' }}>
                        <p className="info-text">Ödeme formu yükleniyor...</p>
                        {/* Buraya bir spinner ekleyebilirsiniz */}
                      </div>
                    )}

                    {loadError && (
                      <div style={{ padding: '20px', textAlign: 'center', border: '1px solid #d32f2f', borderRadius: '4px' }}>
                        <p className="info-text" style={{ color: '#d32f2f', fontWeight: 'bold' }}>
                          {loadError}
                        </p>
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={loadCheckoutForm}
                          disabled={loading}
                          style={{ marginTop: '10px' }}
                        >
                          Tekrar Dene
                        </button>
                      </div>
                    )}
                    
                    {/* KRİTİK KISIM: İyzico'dan gelen HTML içeriği buraya basılır */}
                    {checkoutFormContent && (
                        <div 
                            id="iyzipay-checkout-form"
                            className="responsive"
                            ref={(el) => {
                              if (el && checkoutFormContent && !el.innerHTML) {
                                el.innerHTML = checkoutFormContent;
                                
                                // İçindeki script'leri manuel olarak çalıştır
                                // React'te dangerouslySetInnerHTML ile script'ler otomatik çalışmaz
                                const scripts = el.querySelectorAll('script');
                                scripts.forEach((oldScript) => {
                                  const newScript = document.createElement('script');
                                  Array.from(oldScript.attributes).forEach((attr) => {
                                    newScript.setAttribute(attr.name, attr.value);
                                  });
                                  newScript.textContent = oldScript.textContent;
                                  document.body.appendChild(newScript);
                                  oldScript.remove();
                                });
                              }
                            }}
                        />
                    )}

                        </div>
                      </div>

                      {/* İptal Butonu */}
                      <div className="form-actions" style={{ marginTop: '20px' }}>
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() => navigate(-1)}
                        >
                          İptal
                        </button>
                      </div>
                      
                      {/* Iyzico Logosu */}
                      <div style={{ marginTop: '20px', textAlign: 'center' }}>
                        <img 
                          src="/images/iyzico-logo-pack/checkout_iyzico_ile_ode/TR/Tr_Colored_Horizontal/iyzico_ile_ode_colored_horizontal.svg" 
                          alt="Iyzico ile Öde" 
                          style={{ maxWidth: '100%', height: 'auto', maxHeight: '50px' }}
                        />
                      </div>
                    </div>
              )}
            </div>
          </div>
            </div>
          </div>

      {/* Login Modal */}
      {showLoginModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          zIndex: 9999,
                      display: 'flex',
                      alignItems: 'center',
          justifyContent: 'center',
        }}
        onClick={(e) => {
          // Modal dışına tıklanınca kapatma (sadece çarpı ile kapatılabilir)
          // if (e.target === e.currentTarget) {
          //   setShowLoginModal(false);
          // }
        }}
        >
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            maxWidth: '500px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto',
            position: 'relative',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)'
          }}
          onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowLoginModal(false)}
                        style={{ 
                position: 'absolute',
                top: '15px',
                right: '15px',
                background: 'none',
                border: 'none',
                fontSize: '24px',
                          cursor: 'pointer',
                color: '#666',
                zIndex: 1,
                width: '30px',
                height: '30px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                lineHeight: '1'
              }}
            >
              ×
            </button>
            <div>
              <LoginPage 
                modalMode={true}
                signupSource="checkout"
                onSuccess={() => {
                  setShowLoginModal(false);
                  // Auth state değiştiğinde otomatik olarak isUserAuthenticated true olacak
                  // Sayfada kalacak, yönlendirme yok
                }}
              />
                </div>
                  </div>
                </div>
      )}
    </div>
  );
};

export default CheckoutPage;